import { useCallback, useEffect, useMemo, useState } from 'react';

// "Couldn’t load roles for this app. <server reason>" — specific about what failed and why.
const errorText = (err, fallback) => {
    const detail = err?.response?.data?.message || err?.message;
    return detail ? `${fallback} ${detail}` : fallback;
};

/**
 * Shared loader behind useTenants / useRoles / useAppUsers.
 *
 * Fetches when `key` changes (or `reload()` is called) while `enabled`. A response that
 * arrives after `key` has changed is dropped, so a slow request for the previous app never
 * overwrites the current one. Failures are returned as `error` (a user-facing message)
 * for the page to show; they are never only logged.
 *
 * @param {() => Promise<any[]>} load fetcher; called with no arguments
 * @param {object} opts
 * @param {string} opts.key identity of the request (e.g. `${appId}|${tenantId}`); falsy = idle
 * @param {boolean} [opts.enabled=true]
 * @param {string} opts.idKey field used to build `byId`
 * @param {string} opts.errorMessage fallback error text
 * @returns {{ items: any[], byId: Record<string, any>, loading: boolean, error: string, reload: () => void }}
 */
export default function useAsyncList(load, { key, enabled = true, idKey, errorMessage }) {
    const [state, setState] = useState({ items: [], loading: false, error: '' });
    const [nonce, setNonce] = useState(0);
    const active = Boolean(key) && enabled;

    useEffect(() => {
        if (!active) {
            setState((s) => (s.loading ? { ...s, loading: false } : s));
            return undefined;
        }
        let current = true;
        setState((s) => ({ ...s, loading: true, error: '' }));
        load().then(
            (data) => current && setState({ items: Array.isArray(data) ? data : [], loading: false, error: '' }),
            (err) => current && setState({ items: [], loading: false, error: errorText(err, errorMessage) })
        );
        return () => {
            current = false;
        };
        // `load` closes over the same inputs as `key`; refetch only when those change.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [key, active, nonce]);

    // A different key means a different list: never show the previous app's items.
    const [shownKey, setShownKey] = useState(key);
    if (shownKey !== key) {
        setShownKey(key);
        setState({ items: [], loading: active, error: '' });
    }

    const reload = useCallback(() => setNonce((n) => n + 1), []);
    const byId = useMemo(
        () => Object.fromEntries(state.items.map((item) => [item[idKey], item])),
        [state.items, idKey]
    );

    return { ...state, byId, reload };
}
